import { InnerButton } from '@/modules/InnerButton';
import { Tooltip } from '@/modules/Tooltip';
import { useModalContext } from '../../context';
import { ModalHeaderProps, ModalButton, ModalIcon, ModalBadge } from '@/shared/types/modal';
import { Icon } from '@/modules/Icon';
import { Switch } from '@/modules/Switch';
import { Badge } from '@/modules/Badge';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { InnerButtonProps } from '@/shared/types/button';
import { IconProps } from '@/shared/types/icon';
import { BadgeProps } from '@/shared/types/badge';
import { cn } from '@/shared/utils/cn';

/**
 * Extrae las props de ModalButton que son válidas para InnerButton
 * Excluye las props especiales del Modal (id, closeOnClick, show, label)
 */
const getInnerButtonProps = (
  button: ModalButton,
  overrides?: Partial<InnerButtonProps>
): Partial<InnerButtonProps> => {
  const { id, closeOnClick, show, label, onClick, ...innerButtonProps } = button;
  return {
    ...innerButtonProps,
    ...overrides,
  };
};

/**
 * Extrae las props de ModalIcon que son válidas para Icon
 * Excluye las props especiales del Modal (show) y mapea icon -> name
 * También mapea iconDuotonePrimary/Secondary a colorDuotonePrimary/Secondary para retrocompatibilidad
 */
const getIconProps = (
  iconConfig: ModalIcon,
  overrides?: Partial<IconProps>
): Partial<IconProps> => {
  const {
    show,
    icon,
    iconDuotonePrimary,
    iconDuotoneSecondary,
    iconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary,
    ...iconProps
  } = iconConfig as ModalIcon & {
    iconDuotonePrimary?: string;
    iconDuotoneSecondary?: string;
    iconDuotoneOpacityPrimary?: number;
    iconDuotoneOpacitySecondary?: number;
  };
  return {
    ...iconProps,
    name: icon,
    // Mapear iconDuotone* a colorDuotone* para retrocompatibilidad
    colorDuotonePrimary: iconConfig.colorDuotonePrimary ?? iconDuotonePrimary,
    colorDuotoneSecondary: iconConfig.colorDuotoneSecondary ?? iconDuotoneSecondary,
    opacityDuotonePrimary: iconConfig.opacityDuotonePrimary ?? iconDuotoneOpacityPrimary,
    opacityDuotoneSecondary: iconConfig.opacityDuotoneSecondary ?? iconDuotoneOpacitySecondary,
    ...overrides,
  };
};

/**
 * Extrae las props de ModalBadge que son válidas para Badge
 * Excluye las props especiales del Modal (id, show)
 */
const getBadgeProps = (badge: ModalBadge, overrides?: Partial<BadgeProps>): Partial<BadgeProps> => {
  const { id, show, ...badgeProps } = badge;
  return {
    ...badgeProps,
    ...overrides,
  };
};
export const ModalHeader = ({
  children,
  showCloseButton,
  closeButton: closeButtonProp,
  title,
  tabs = [],
  defaultValueTab,
  onChangeTab,
  headerButton: headerButtonProp,
  headerButtons: headerButtonsProp,
  badge: badgeProp,
  badges: badgesProp,
  // Props legacy (mantener para retrocompatibilidad)
  headerButtonIcon,
  headerButtonIconDuotonePrimary,
  headerButtonIconDuotoneSecondary,
  headerButtonIconDuotoneOpacityPrimary,
  headerButtonIconDuotoneOpacitySecondary,
  headerButtonOnClick,
  badgeIcon,
  badgeIconDuotonePrimary,
  badgeIconDuotoneSecondary,
  badgeIconDuotoneOpacityPrimary,
  badgeIconDuotoneOpacitySecondary,
  badgeLabel,
}: ModalHeaderProps) => {
  const {
    onClose,
    showCloseButton: defaultShowCloseButton,
    closeButton: contextCloseButton,
    // Nuevo sistema procesado
    processedIcon,
    processedHeaderButtons,
    processedBadges,
    // Props legacy (mantener para retrocompatibilidad)
    icon,
    iconDuotonePrimary: defaultIconDuotonePrimary,
    iconDuotoneSecondary: defaultIconDuotoneSecondary,
    iconDuotoneOpacityPrimary: defaultIconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: defaultIconDuotoneOpacitySecondary,
    tabs: defaultTabs,
    defaultValueTab: defaultDefaultValueTab,
    onChangeTab: defaultOnChangeTab,
    tabsSwitchProps: defaultTabsSwitchProps,
    currentTab: defaultCurrentTab = undefined,
    headerButtonIcon: defaultHeaderButtonIcon,
    headerButtonIconDuotonePrimary: defaultHeaderButtonIconDuotonePrimary,
    headerButtonIconDuotoneSecondary: defaultHeaderButtonIconDuotoneSecondary,
    headerButtonIconDuotoneOpacityPrimary: defaultHeaderButtonIconDuotoneOpacityPrimary,
    headerButtonIconDuotoneOpacitySecondary: defaultHeaderButtonIconDuotoneOpacitySecondary,
    headerButtonOnClick: defaultHeaderButtonOnClick,
    badgeIcon: defaultBadgeIcon,
    badgeIconDuotonePrimary: defaultBadgeIconDuotonePrimary,
    badgeIconDuotoneSecondary: defaultBadgeIconDuotoneSecondary,
    badgeIconDuotoneOpacityPrimary: defaultBadgeIconDuotoneOpacityPrimary,
    badgeIconDuotoneOpacitySecondary: defaultBadgeIconDuotoneOpacitySecondary,
    badgeLabel: defaultBadgeLabel,
    sectionPadding = 'md',
  } = useModalContext();

  const shouldShowCloseButton = showCloseButton ?? defaultShowCloseButton;

  const closeButtonResolved = closeButtonProp ?? contextCloseButton;
  const showCloseTooltip = closeButtonResolved?.tooltip === true;
  const closeTooltipLabel = closeButtonResolved?.tooltipLabel ?? 'Cerrar';
  const closeTooltipPosition = closeButtonResolved?.tooltipPosition ?? 'top';
  const closeTooltipAlignment = closeButtonResolved?.tooltipAlignment ?? 'center';

  // Prioridad: props directas > procesadas del contexto
  const headerButtonsToRender =
    headerButtonsProp ||
    (headerButtonProp ? [headerButtonProp] : null) ||
    processedHeaderButtons ||
    [];
  const badgesToRender = badgesProp || (badgeProp ? [badgeProp] : null) || processedBadges || [];

  // Icono: usar processedIcon si está disponible, sino usar props legacy
  const iconToShow = processedIcon?.icon || icon;
  const shouldShowIcon = processedIcon ? processedIcon.show !== false : !!icon;

  // TABS
  const shouldTabs = tabs?.length > 0 ? tabs : defaultTabs;
  const shouldShowTabs = shouldTabs?.length ?? false;
  const shouldOnChangeTab = onChangeTab ?? defaultOnChangeTab;
  const shouldDefaultValueTab = defaultValueTab ?? defaultDefaultValueTab;

  // Usar el onChangeTab del contexto que ya maneja el estado del tab actual
  const handleChangeTab = (value: string) => {
    if (shouldOnChangeTab) {
      shouldOnChangeTab(value);
    }
  };

  const sectionPaddingClass = sectionPadding === 'sm' ? 'ui-p-8' : 'ui-p-16';

  return (
    <div
      className={cn(
        'ui-relative ui-flex ui-flex-wrap ui-items-center ui-justify-between ui-gap-x-md ui-border-b ui-border-default',
        sectionPaddingClass
      )}
    >
      {shouldShowIcon && iconToShow && processedIcon && (
        <Icon
          background
          {...(getIconProps(processedIcon, {
            size: processedIcon.size || 'sm',
            color: processedIcon.color || 'blue',
            colorDuotonePrimary: processedIcon.iconDuotonePrimary ?? defaultIconDuotonePrimary,
            colorDuotoneSecondary:
              processedIcon.iconDuotoneSecondary ?? defaultIconDuotoneSecondary,
            opacityDuotonePrimary:
              processedIcon.iconDuotoneOpacityPrimary ?? defaultIconDuotoneOpacityPrimary,
            opacityDuotoneSecondary:
              processedIcon.iconDuotoneOpacitySecondary ?? defaultIconDuotoneOpacitySecondary,
          }) as IconProps)}
        />
      )}
      <div className="ui-flex ui-flex-1 ui-items-center ui-gap-8">
        {title ? (
          <h2 className="ui-text-heading-h3 ui-font-heading-h3 ui-leading-heading-h3 ui-tracking-heading-h3">
            {title}
          </h2>
        ) : (
          children
        )}
      </div>
      <div className="ui-z-10 ui-flex ui-items-center ui-gap-16">
        {/* Badges del nuevo sistema */}
        {badgesToRender.map((badge, index) => (
          <Badge
            key={badge.id || `badge-${index}`}
            {...getBadgeProps(badge, {
              label: badge.label,
              color: badge.color || 'accent',
              size: badge.size || 'sm',
            })}
          />
        ))}

        {/* Botones del header del nuevo sistema */}
        {headerButtonsToRender.map((button, index) => (
          <InnerButton
            key={button.id || `header-button-${index}`}
            aria-label={button.label || `Header button ${index + 1}`}
            {...getInnerButtonProps(button, {
              onClick: button.onClick
                ? (event: React.MouseEvent<HTMLElement>) =>
                    button.onClick?.(event as React.MouseEvent<HTMLButtonElement>)
                : undefined,
              theme: button.theme || 'solid',
              color: button.color || 'secondary',
              pill: button.pill || 'pill',
              icon: button.icon || button.leftSlot,
              size: button.size || 'xs',
            })}
          >
            {button.label}
          </InnerButton>
        ))}

        {shouldShowCloseButton &&
          (showCloseTooltip ? (
            <InnerButton
              onClick={onClose}
              aria-label="Cerrar modal"
              theme="solid"
              color="secondary"
              pill="pill"
              icon={faXmark}
              size="xs"
              tooltipProps={{
                label: closeTooltipLabel,
                position: closeTooltipPosition,
                alignment: closeTooltipAlignment,
              }}
            />
          ) : (
            <InnerButton
              onClick={onClose}
              aria-label="Cerrar modal"
              theme="solid"
              color="secondary"
              pill="pill"
              icon={faXmark}
              size="xs"
            />
          ))}
      </div>

      {shouldShowTabs ? (
        <div className="ui-z-0 ui-mt-2 ui-flex ui-w-full ui-justify-center ui-overflow-x-auto sm:ui-absolute">
          <Switch
            {...defaultTabsSwitchProps}
            options={shouldTabs ?? []}
            defaultValue={defaultCurrentTab ?? shouldDefaultValueTab}
            onChange={(value) => {
              handleChangeTab(value);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

ModalHeader.displayName = 'Modal.Header';
